import React, { useState } from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormTrigger,
	UseFormWatch,
} from 'react-hook-form';

import { Button, Confirm, Input, Modal, Select, SpinnerOverlay, TextArea } from '@components/common';

import { useQueryPortalAvailableProjects } from '@hooks/portal/projects/portalProjects/useQueryPortalAvailableProjects';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IPortalTaskDTO } from '@interfaces/portal/dto/taskManagementDTO';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { MAX_80_LENGTH, MAX_1000_LENGTH } from '@constants/validationMessages';
import {
	fieldsToTestBasicInfo,
	tabsNames,
	taskFormFields,
} from '../constants';
import { changeProjectConfirmDescription, changeProjectConfirmText } from './constants';

import styles from './BasicInfo.module.scss';

interface IBasicInfoProps {
	isEditMode: boolean;
	isViewMode: boolean;
	register: UseFormRegister<FieldValues>;
	errors: FieldErrors;
	setValue: UseFormSetValue<FieldValues>;
	trigger: UseFormTrigger<FieldValues>;
	control: Control;
	isBasicInfoTabError: boolean;
	setCurrentTab: (value: string) => void;
	portalTask?: IPortalTaskDTO;
	watch: UseFormWatch<FieldValues>;
	clearForm: () => void;
	isDirty: boolean;
}

const BasicInfo = ({
	control,
	errors,
	register,
	setValue,
	isEditMode,
	isBasicInfoTabError,
	trigger,
	setCurrentTab,
	portalTask,
	isViewMode,
	clearForm,
	isDirty,
	watch,
}: IBasicInfoProps) => {
	const [isChangeProjectConfirm, setIsChangeProjectConfirm] = useState(false);
	const [projectToSelect, setProjectToSelect] = useState<TSelectOption<string>>(null);

	const descriptionValue = watch(taskFormFields.description);

	const closeChangeProjectConfirm = () => {
		setIsChangeProjectConfirm(false);
		setProjectToSelect(null);
	};

	const submitChangeProjectConfirm = () => {
		clearForm();

		setValue(taskFormFields.project, projectToSelect);

		setIsChangeProjectConfirm(false);
		setProjectToSelect(null);
	};

	const { portalAvailableProjects, isLoadingPortalAvailableProjects } = useQueryPortalAvailableProjects();

	const onSubmit = async () => {
		const isValid = await trigger(fieldsToTestBasicInfo);

		if (!isValid) return;

		if (!isEditMode) setCurrentTab(tabsNames.details);
	};

	const onProjectChange = (option: TSelectOption<string>) => {
		if (!isDirty) {
			setValue(taskFormFields.project, option);

			return;
		}

		setIsChangeProjectConfirm(true);
		setProjectToSelect(option);
	};

	const descriptionLength = descriptionValue?.trim().length || 0;
	const titleLength = watch(taskFormFields.title)?.trim().length || 0;

	return (
		<>
			{isLoadingPortalAvailableProjects && <SpinnerOverlay />}
			{!isEditMode && !isViewMode
				&& <div className={styles.project}>
					<Select
						control={control}
						errorMessage={errors.project?.message}
						handleChange={onProjectChange}
						isError={!!errors?.project}
						label='Project'
						name={taskFormFields.project}
						options={portalAvailableProjects}
						placeholder='Choose project'
						required
					/>
				</div>
			}
			{(isEditMode || isViewMode) && portalTask
				&& <div className={styles.taskInfoContainer}>
					<div className={styles.taskInfo}>
						<div className={styles.taskInfoTitle}>Project:</div>
						<div className={styles.taskInfoValue}>{portalTask.projectName}</div>
					</div>
					<div className={styles.taskInfo}>
						<div className={styles.taskInfoTitle}>Task ID:</div>
						<div className={styles.taskInfoValue}>{portalTask.taskId}</div>
					</div>
					<div className={styles.taskInfo}>
						<div className={styles.taskInfoTitle}>Task creator:</div>
						<div className={styles.taskInfoValue}>{portalTask.creator?.fullName || ''}</div>
					</div>
				</div>
			}
			<div className={styles.title}>
				<Input
					disabled={isViewMode}
					errorMessage={errors?.title?.message}
					isError={!!errors?.title}
					isMaxLengthCounter
					label='Title'
					maxLength={MAX_80_LENGTH}
					name={taskFormFields.title}
					placeholder='Enter title'
					register={register}
					required
					setValue={setValue}
					valueLength={titleLength}
				/>
			</div>
			<div className={styles.description}>
				<TextArea
					disabled={isViewMode}
					errorMessage={errors.description?.message}
					isMaxLengthCounter
					isNotResizable
					label='Task Description'
					maxLength={MAX_1000_LENGTH}
					name={taskFormFields.description}
					placeholder='Enter description'
					register={register}
					setValue={setValue}
					value={descriptionValue}
					valueLength={descriptionLength}
					withUseForms
				/>
			</div>
			{!isEditMode && !isViewMode
				&& <div className={styles.submitButton}>
					<Button
						disabled={isBasicInfoTabError}
						onClick={onSubmit}
						text='Next'
					/>
				</div>
			}
			{isChangeProjectConfirm && (
				<Modal
					isOpen={isChangeProjectConfirm}
					onClose={closeChangeProjectConfirm}
					title='Confirm action'
					width={MODAL_WIDTH_480}
				>
					<Confirm
						onClose={closeChangeProjectConfirm}
						onSubmit={submitChangeProjectConfirm}
						question={changeProjectConfirmText}
					>
						{changeProjectConfirmDescription}
					</Confirm>
				</Modal>
			)}
		</>
	);
};

export default BasicInfo;
